/**
 * Model preflight and freshness checks.
 *
 * Two related, provider-agnostic concerns, kept as pure functions so they can
 * be unit-tested without the real registry:
 *
 * 1. Preflight: verify that configured model references (defaults, smart-routing
 *    pins) still resolve against the model registry. Model IDs churn as
 *    providers add/remove models, so a default can silently point at a removed
 *    ID and only fail at runtime.
 *
 * 2. Freshness: given a configured model and the models available for its
 *    provider, detect strictly-newer siblings in the same family so we can nudge
 *    the user (e.g. "your OpenAI default gpt-5.4 is behind gpt-5.6").
 */

/** True for an 8-digit date-like token (a dated snapshot, e.g. 20250514). */
function isDateToken(token: string): boolean {
	return /^\d{8}$/.test(token);
}

/** True when a token is purely a version number (digits and dots), not a date. */
function isVersionToken(token: string): boolean {
	return /^\d+(\.\d+)*$/.test(token) && !isDateToken(token);
}

export interface ParsedModelId {
	/** Non-numeric leading tokens joined by "-" (e.g. "gpt", "claude-sonnet"). */
	stem: string;
	/** Flattened numeric version components (e.g. [5, 6] for gpt-5.6). */
	version: number[];
	/** Sorted variant tokens (e.g. ["mini"]). */
	variants: string[];
	/** Whether the id contains a dated snapshot token. */
	dated: boolean;
}

/**
 * Parse a model id into a family stem, a numeric version, and variant tokens.
 * Deliberately simple and conservative: version tokens are dotted numbers,
 * dated snapshots are flagged, everything after the first version token that
 * isn't a version is treated as a variant.
 */
export function parseModelId(id: string): ParsedModelId {
	const tokens = id.split("-");
	const stemTokens: string[] = [];
	const version: number[] = [];
	const variants: string[] = [];
	let dated = false;
	let seenVersion = false;

	for (const token of tokens) {
		if (isDateToken(token)) {
			dated = true;
			continue;
		}
		if (isVersionToken(token)) {
			seenVersion = true;
			for (const part of token.split(".")) {
				version.push(Number.parseInt(part, 10));
			}
			continue;
		}
		if (!seenVersion) {
			stemTokens.push(token);
		} else {
			variants.push(token.toLowerCase());
		}
	}

	return {
		stem: stemTokens.join("-"),
		version,
		variants: variants.sort(),
		dated,
	};
}

/** Compare two version arrays component-wise (shorter padded with zeros). */
function compareVersions(a: number[], b: number[]): number {
	const len = Math.max(a.length, b.length);
	for (let i = 0; i < len; i++) {
		const av = a[i] ?? 0;
		const bv = b[i] ?? 0;
		if (av !== bv) return av - bv;
	}
	return 0;
}

function sameVariants(a: string[], b: string[]): boolean {
	if (a.length !== b.length) return false;
	return a.every((v, i) => v === b[i]);
}

/**
 * Given the currently configured model id and the set of model ids available
 * for the same provider, return the ids that are a strictly-newer version of
 * the same family (same stem, same variant set), newest first.
 *
 * Conservative by design: only clean (non-dated) candidates are suggested, so
 * we never nudge someone from a stable alias onto a dated snapshot.
 */
export function findNewerModels(currentId: string, candidateIds: readonly string[]): string[] {
	const current = parseModelId(currentId);
	if (current.version.length === 0) return [];

	const newer = candidateIds
		.filter((id) => id !== currentId)
		.map((id) => ({ id, parsed: parseModelId(id) }))
		.filter(
			({ parsed }) =>
				!parsed.dated &&
				parsed.stem === current.stem &&
				sameVariants(parsed.variants, current.variants) &&
				compareVersions(parsed.version, current.version) > 0,
		)
		.sort((a, b) => compareVersions(b.parsed.version, a.parsed.version));

	return newer.map(({ id }) => id);
}

export interface PreflightProblem {
	provider: string;
	/** The reference kind that failed to resolve. */
	kind: "default" | "smart-routing";
	/** Extra detail for smart-routing tier, when applicable. */
	tier?: string;
	modelId: string;
	message: string;
}

export interface FreshnessNote {
	provider: string;
	currentId: string;
	/** Newer sibling ids in the same family, newest first. */
	newer: string[];
}

/**
 * Check that every configured default resolves against the registry, and
 * collect freshness notes for defaults that have newer siblings.
 *
 * `providerModelIds` maps a provider to the model ids it exposes in the
 * registry. `defaults` maps a provider to its configured default model id.
 */
export function checkDefaults(
	defaults: Record<string, string>,
	providerModelIds: Record<string, readonly string[]>,
): { problems: PreflightProblem[]; freshness: FreshnessNote[] } {
	const problems: PreflightProblem[] = [];
	const freshness: FreshnessNote[] = [];

	for (const [provider, modelId] of Object.entries(defaults)) {
		const available = providerModelIds[provider] ?? [];
		if (!available.includes(modelId)) {
			problems.push({
				provider,
				kind: "default",
				modelId,
				message: `default model "${modelId}" is not in the registry for provider "${provider}"`,
			});
			continue;
		}
		const newer = findNewerModels(modelId, available);
		if (newer.length > 0) {
			freshness.push({ provider, currentId: modelId, newer });
		}
	}

	return { problems, freshness };
}

/**
 * Validate user-pinned smart-routing model references against the registry.
 * `pins` maps a tier to a "provider/model-id" reference (as stored in settings).
 * A reference resolves when its model id exists for its provider.
 */
export function checkSmartRoutingPins(
	pins: Record<string, string | undefined>,
	providerModelIds: Record<string, readonly string[]>,
): PreflightProblem[] {
	const problems: PreflightProblem[] = [];

	for (const [tier, ref] of Object.entries(pins)) {
		if (!ref) continue;
		const slash = ref.indexOf("/");
		const provider = slash >= 0 ? ref.slice(0, slash) : "";
		const modelId = slash >= 0 ? ref.slice(slash + 1) : ref;
		const available = providerModelIds[provider] ?? [];
		if (!provider || !available.includes(modelId)) {
			problems.push({
				provider,
				kind: "smart-routing",
				tier,
				modelId: ref,
				message: `smart-routing ${tier} pin "${ref}" does not resolve in the registry`,
			});
		}
	}

	return problems;
}
